/**
 * Client Product Analytics Helper
 */
import { type AnalyticsEvent } from '../analytics/analytics.helper';
import { TrackedEvent, TrackedEventCase } from '../analytics/event-types';
import { type GTMCustomEvent } from '../analytics/gtm/gtm-types';
import { buildGTMProduct } from '../analytics/gtm/gtm-utils.helper';

type ProductInfo = {
	product: any;
	variant: any;
};

export type ProductOptionsAccepted = { variantId: number; quantity: number };

export type ProductOptionsDeclined = { declined: boolean };

export type SelectedNestedProductOption = ProductOptionsAccepted | ProductOptionsDeclined;

export const isTrackingSelectionDeclined = (param: SelectedNestedProductOption): param is ProductOptionsDeclined => 'declined' in param;

export const getGASelectVariation = (variationName: string, info: ProductInfo): AnalyticsEvent => {
	return {
		data: {
			type: TrackedEventCase.PRODUCT_SELECT_VARIATION,
			event: TrackedEvent.PRODUCT_CONFIGURATION,
			ecommerce: {
				click: {
					actionField: { list: 'Variation list', variation: variationName },
					products: [buildGTMProduct(info.product, info.variant, 0)]
				}
			}
		}
	};
};

export const getGASelectConfigurationOption = (optionName: string, info: ProductInfo, optionValue?: string | null): AnalyticsEvent => {
	return {
		data: {
			type: TrackedEventCase.PRODUCT_SELECT_CONFIGURATION_OPTION,
			event: TrackedEvent.PRODUCT_CONFIGURATION,
			ecommerce: {
				click: {
					actionField: { list: 'Configuration Option List', option: optionName, ...(optionValue ? { value: optionValue } : {}) },
					products: [buildGTMProduct(info.product, info.variant, 0)]
				}
			}
		}
	};
};

export const getGASelectConfigurationNestedOption = (options: SelectedNestedProductOption, info: ProductInfo): AnalyticsEvent => {
	return {
		data: {
			type: isTrackingSelectionDeclined(options)
				? TrackedEventCase.DECLINED_PRODUCT_SELECT_CONFIGURATION_NESTED_OPTION
				: TrackedEventCase.PRODUCT_SELECT_CONFIGURATION_NESTED_OPTION,
			event: TrackedEvent.PRODUCT_CONFIGURATION,
			ecommerce: {
				click: {
					actionField: { list: 'Configuration Nested Options', ...options },
					products: [buildGTMProduct(info.product, info.variant, 0)]
				}
			}
		}
	};
};

export const buildGTMRemoveConfigurationNestedOption = (): GTMCustomEvent => ({
	event: TrackedEvent.PRODUCT_CONFIGURATION,
	type: TrackedEventCase.PRODUCT_SELECT_CONFIGURATION_OPTION_REMOVE_ITEM,
	ecommerce: {
		click: {
			actionField: { list: 'Configuration Nested Options' }
		}
	}
});

export const buildGAConfigureClick = () => ({ event: TrackedEvent.STICKY_NAV_CONFIGURE });

export const buildGAFindShowroomClick = () => ({ event: TrackedEvent.STICKY_NAV_FIND_SHOWROOM });

export const buildGAButtonSelection = (buttonLabel) => ({ event: TrackedEvent.STICKY_NAV_SELECTION_CLICKED, label: buttonLabel });
